/**
 * Phase 6 — top-level invite-token gate.
 *
 * Renders `<children />` only when a valid bearer token is present in
 * localStorage. Otherwise renders the "InviteRequired" screen with an
 * optional manual paste box.
 *
 * Boot sequence:
 *   1. captureTokenFromUrl() — if the URL has `?token=…`, save & clean.
 *   2. validateToken(stored) — round-trip to `/api/access/validate`.
 *   3. If valid → render children. Otherwise → invite screen.
 *   4. The apiClient response interceptor dispatches an
 *      `"access-token-invalidated"` window event on any 401 so this
 *      component can re-render the invite screen without a page reload.
 */
import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldAlert, KeyRound, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  captureTokenFromUrl,
  getStoredToken,
  setStoredToken,
  clearStoredToken,
  validateToken,
} from "@/lib/accessApi";

const STATE = {
  CHECKING: "checking",
  ALLOWED:  "allowed",
  BLOCKED:  "blocked",
};

export default function AccessGate({ children }) {
  const [state, setState] = useState(STATE.CHECKING);
  const [errorMsg, setErrorMsg] = useState("");
  const [pasteValue, setPasteValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const runValidation = useCallback(async (tokenOverride) => {
    const tok = tokenOverride ?? getStoredToken();
    if (!tok) {
      setState(STATE.BLOCKED);
      return false;
    }
    try {
      const res = await validateToken(tok);
      if (res?.valid) {
        setStoredToken(tok);
        setErrorMsg("");
        setState(STATE.ALLOWED);
        // T1 — broadcast a "validated" pulse so deep-linked pages
        // (Quick Picker etc.) that mounted before the token landed in
        // localStorage can re-fire their initial loader without
        // waiting for a remount. Pages without this listener are
        // unaffected — they were already mounted post-gate by the
        // existing CHECKING → ALLOWED render branch.
        try {
          window.dispatchEvent(new CustomEvent("access-token-validated"));
        } catch (e) {
          // Older browsers may not support CustomEvent — non-fatal.
          // eslint-disable-next-line no-console
          console.warn("AccessGate: failed to dispatch validated event", e);
        }
        return true;
      }
      clearStoredToken();
      setState(STATE.BLOCKED);
      setErrorMsg(
        tokenOverride
          ? "That access token isn't recognised. Double-check your invite link."
          : "",
      );
      return false;
    } catch (e) {
      // Network failure — fall through to blocked but show a friendly hint.
      setState(STATE.BLOCKED);
      setErrorMsg(
        "Couldn't reach the server to validate your access token. Check your connection.",
      );
      return false;
    }
  }, []);

  // Initial mount — capture `?token=…` if present, then validate.
  useEffect(() => {
    const captured = captureTokenFromUrl();
    runValidation(captured || undefined);
  }, [runValidation]);

  // Re-validate when the api client signals a 401 (token was rotated /
  // revoked). Wipes the stored token and bounces to the invite screen.
  useEffect(() => {
    const handler = () => setState(STATE.BLOCKED);
    window.addEventListener("access-token-invalidated", handler);
    return () => window.removeEventListener("access-token-invalidated", handler);
  }, []);

  const handlePasteSubmit = async (e) => {
    e.preventDefault();
    const v = pasteValue.trim();
    if (!v) return;
    setSubmitting(true);
    setStoredToken(v);
    const ok = await runValidation(v);
    setSubmitting(false);
    if (ok) setPasteValue("");
  };

  if (state === STATE.CHECKING) {
    return (
      <div
        className="flex h-screen w-full items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100"
        data-testid="access-gate-checking"
      >
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Verifying access…
        </div>
      </div>
    );
  }

  if (state === STATE.ALLOWED) {
    return children;
  }

  // BLOCKED
  return (
    <div
      className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 px-4 py-12"
      data-testid="invite-required-screen"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-lg">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900">
              Invite link required
            </h1>
            <p className="text-xs text-muted-foreground">
              This workspace is private.
            </p>
          </div>
        </div>

        <p className="mb-5 text-sm leading-relaxed text-slate-700">
          This VoyageMenu workspace is private. Please use the invite link
          your team shared with you to access it.
        </p>

        {errorMsg && (
          <div
            className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800"
            data-testid="invite-error"
          >
            {errorMsg}
          </div>
        )}

        <form onSubmit={handlePasteSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label
              htmlFor="invite-token-paste"
              className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-600"
            >
              <KeyRound className="h-3 w-3" />
              Or paste your access token here
            </Label>
            <Input
              id="invite-token-paste"
              type="text"
              value={pasteValue}
              onChange={(e) => setPasteValue(e.target.value)}
              placeholder="32-character token"
              autoComplete="off"
              spellCheck={false}
              data-testid="invite-token-input"
            />
          </div>
          <Button
            type="submit"
            disabled={!pasteValue.trim() || submitting}
            className="w-full"
            data-testid="invite-token-submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Unlock workspace"
            )}
          </Button>
        </form>

        <div className="mt-6 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
          <Mail className="mt-0.5 h-3 w-3 shrink-0 opacity-70" />
          <p className="leading-relaxed">
            Lost your invite? Contact your VoyageMenu administrator and ask
            them to re-share the access link from the Settings → Access tab.
          </p>
        </div>
      </div>
    </div>
  );
}
